import { MenuItem, Select, SelectChangeEvent, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { Box } from "@mui/system"
import { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLocationDot, faUser, faWallet } from "@fortawesome/pro-light-svg-icons"

import { useDateTime } from "../../../../localization/useDateTime"
import { getOrderPerson } from "../../../../../shared/utils/getOrderPerson"
import { sanitizeSearchQuery } from "../../../../../lib/stringLib"
import { StandardDotSeparator } from "../../../../../shared/components/separator/StandardDotSeparator"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { getTranslationIdForEnum } from "../../../../localization/localizationLib"
import { useMoney } from "../../../../currency/useMoney"
import { toDineroOrUndefined } from "../../../../currency/currencyLib"
import { AnyMeeting, hasDeliveryDetails } from "../../../../nonAdminUser/reports/MeetingsReceipt/UserMeetingsReceipt"
import { AnyMeetingServiceOrder } from "../../production/meetingService/components/OrderPackageSlip"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import { useAppSelector } from "../../../../store/storeHooks"
import { selectUserType } from "../../../../login/loginSelectors"
import { UserType } from "../../../../login/loginTypes"

import { SearchOrdersInPopup } from "./SearchOrdersInPopup"
import {
    getCanteenNameFromOrder,
    getCustomerEmailFromOrder,
    getDeliveryTimeFromOrder,
    getMonetaryAmountFromOrder,
    getPaymentMethodFromOrder,
    hasCustomer,
    hasOrganizers,
} from "./receiptDropdownUtils"

const useStyles = makeStyles()((theme) => ({
    headerText: {
        fontSize: "1.125rem",
        fontWeight: 500,
        color: theme.palette.common.black,
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
    orderSelect: {
        minWidth: "12.5rem",
        width: "fit-content",
        maxWidth: "100%",
        fontSize: "1rem",
        fontWeight: 500,
        color: theme.palette.common.black,
        borderRadius: ".5rem",
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: tailwindPalette.slate[300],
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: tailwindPalette.slate[400],
        },
        "& .MuiSelect-select": {
            paddingTop: ".5rem",
            paddingBottom: ".5rem",
        },
    },
    orderSelectHiddenOnMobile: {
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
    layoutBlankFill: {
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
    selectedSlip: {
        fontWeight: 500,
    },
    headerDropdownMenuItem: {
        display: "flex",
        flexDirection: "row",
        alignItems: "start",
        gap: ".75rem",
        width: "100%",
        marginRight: 0,
        paddingRight: "1rem",
        zIndex: 1,
    },
    walletIconContainer: {
        padding: "0.25rem",
    },
    walletIcon: {
        color: tailwindPalette.slate[600],
        width: "1.5rem",
        height: "1.5rem",
    },
    dropdownContent: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        flex: 1,
    },
    dropdownTopRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1.5rem",
        minWidth: 0,
    },
    dropdownTopRowRight: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        flexShrink: 0,
    },
    dropdownBottomRow: {
        display: "flex",
        alignItems: "center",
        gap: ".375rem",
        minWidth: 0,
    },
    dropdownSecondaryIcon: {
        color: tailwindPalette.slate[500],
        width: ".75rem",
        height: ".75rem",
    },
    dropdownIconCell: {
        display: "flex",
        alignItems: "center",
        gap: "0.375rem",
        minWidth: 0,
    },
    dropdownIcon: {
        color: tailwindPalette.slate[500],
        width: "1rem",
        height: "1rem",
    },
    dropdownIdText: {
        whiteSpace: "nowrap",
    },
    dropdownMetaText: {
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    dropdownSecondaryText: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    dropdownAmountText: {
        whiteSpace: "nowrap",
    },
    orderSelectItem: {
        maxHeight: 350,
        borderRadius: "0.5rem",
        minWidth: "40rem !important",
        ".MuiMenuItem-root:hover": {
            backgroundColor: tailwindPalette.slate[50],
        },
        ".MuiMenu-list": {
            padding: 0,
        },
    },
    menuItem: {
        padding: "0.75rem 1rem",
    },
}))

type OrdersType = AnyMeeting[] | AnyMeetingServiceOrder[]
type OrderType = AnyMeeting | AnyMeetingServiceOrder
type OrderIdType = OrderType["id"]
type SlipVariant = "slip" | "receipt" | "pos"

const SelectDisplayReceiptComponent = ({
    value,
    meetings,
}: Readonly<{ value: OrderIdType; meetings: AnyMeeting[] }>) => {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const meeting = meetings.find((m) => m.id === value)

    return (
        <Typography className={classes.selectedSlip}>
            {meeting?.meetingDetails.meetingName || ""}, #{meeting?.id}
            {meeting &&
                hasDeliveryDetails(meeting) &&
                meeting?.deliveryDetails?.time &&
                `, ${dateTimeFactory.formatDate(dateTimeFactory.fromApi(meeting?.deliveryDetails?.time))}`}
        </Typography>
    )
}

const SelectDisplayPosReceiptComponent = ({
    value,
    orders,
}: Readonly<{ value: OrderIdType; orders: AnyMeeting[] }>) => {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const order = orders.find((m) => m.id === value)
    const deliveryTime =
        order && "deliveries" in order && Array.isArray(order.deliveries)
            ? order.deliveries[0]?.deliveryTime
            : undefined
    const orderDate = deliveryTime
        ? dateTimeFactory.formatCustom(dateTimeFactory.fromApi(deliveryTime), "MMM d'th', yyyy")
        : ""

    return (
        <LocalizedStrict
            id="receipt-order-label"
            vars={{
                orderId: order?.id.toString() ?? "",
                orderDate,
            }}
        >
            <Typography className={classes.headerText}>
                Receipt #{order?.id.toString()}, {orderDate}
            </Typography>
        </LocalizedStrict>
    )
}

const SelectDisplaySlipComponent = ({
    value,
    orders,
}: Readonly<{ value: OrderIdType; orders: AnyMeetingServiceOrder[] }>) => {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const order = orders.find((m) => m.id === value)

    return (
        <Typography className={classes.selectedSlip}>
            {order?.meetingDetails.meetingName || ""}, #{order?.id},{" "}
            {dateTimeFactory.formatDate(dateTimeFactory.fromApi(order?.deliveryDetails.time ?? ""))}
        </Typography>
    )
}

const SelectedOrderTitle = ({
    value,
    orders,
    variant,
}: Readonly<{ value: OrderIdType; orders: OrdersType; variant: SlipVariant }>) => {
    if (variant === "pos") {
        return <SelectDisplayPosReceiptComponent value={value} orders={orders as AnyMeeting[]} />
    }
    if (variant === "receipt") {
        return <SelectDisplayReceiptComponent value={value} meetings={orders as AnyMeeting[]} />
    }
    return <SelectDisplaySlipComponent value={value} orders={orders as AnyMeetingServiceOrder[]} />
}

export const ReceiptDisplayDropdownMenu = ({ order }: { order: OrderType }) => {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()
    const userType = useAppSelector(selectUserType)
    const isNormalUser = userType === UserType.USER

    const customerDisplayName = useMemo(() => {
        if (hasOrganizers(order)) {
            return getOrderPerson(order.organizers).person?.name
        }
        if (hasCustomer(order)) {
            return order.customer.displayName
        }
        return undefined
    }, [order])

    const customerEmail = useMemo(() => getCustomerEmailFromOrder(order), [order])
    const canteenName = useMemo(() => getCanteenNameFromOrder(order), [order])

    const deliveryTime = getDeliveryTimeFromOrder(order)
    const deliveryDate = deliveryTime ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(deliveryTime)) : undefined

    const totalAmount = useMemo(() => {
        const monetaryAmount = getMonetaryAmountFromOrder(order)
        if (!monetaryAmount) return undefined
        try {
            const dineroAmount = toDineroOrUndefined(monetaryAmount)
            return dineroAmount ? moneyFactory.format(dineroAmount) : monetaryAmount.formatted
        } catch {
            return monetaryAmount.formatted
        }
    }, [order, moneyFactory])

    const paymentMethodLabel = useMemo(() => {
        const paymentMethod = getPaymentMethodFromOrder(order)
        if (!paymentMethod) return undefined
        return l10n.getStringForEnum("receipt-payment-method", paymentMethod, paymentMethod)
    }, [order, l10n])

    const secondaryIcon = isNormalUser ? faLocationDot : faUser
    const secondaryText = isNormalUser
        ? canteenName
        : customerDisplayName && customerEmail
          ? `${customerDisplayName} (${customerEmail})`
          : (customerDisplayName ?? customerEmail)

    const walletIconElement = (
        <Box component="span" className={classes.walletIconContainer}>
            <FontAwesomeIcon icon={faWallet} className={classes.walletIcon} />
        </Box>
    )

    return (
        <Box className={classes.headerDropdownMenuItem}>
            {paymentMethodLabel ? (
                <Tooltip title={paymentMethodLabel} arrow>
                    {walletIconElement}
                </Tooltip>
            ) : (
                walletIconElement
            )}
            <Box className={classes.dropdownContent}>
                <Box className={classes.dropdownTopRow}>
                    <StandardDotSeparator>
                        <Typography color="textPrimary" className={classes.dropdownIdText}>
                            {`# ${order?.id}`}
                        </Typography>
                        {totalAmount && (
                            <Typography color="textPrimary" className={classes.dropdownAmountText}>
                                {totalAmount}
                            </Typography>
                        )}
                    </StandardDotSeparator>
                    {deliveryDate && (
                        <Box className={classes.dropdownTopRowRight}>
                            <Typography color="textPrimary" className={classes.dropdownMetaText}>
                                {deliveryDate}
                            </Typography>
                        </Box>
                    )}
                </Box>
                {secondaryText && (
                    <Box className={classes.dropdownBottomRow}>
                        <FontAwesomeIcon icon={secondaryIcon} className={classes.dropdownSecondaryIcon} />
                        <Typography variant="body2" color="textSecondary" className={classes.dropdownSecondaryText}>
                            {secondaryText}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

// pos passes IMyOrderDetails here, meeting flows pass MeetingDetailsOrder
type OrderDetailsForTitle = Readonly<{
    id: number
    deliveries?: ReadonlyArray<Readonly<{ deliveryTime?: string }>>
    meetingDetails?: Readonly<{ meetingName: string }>
}>

type SlipModalHeaderProps = {
    isAuthenticated: boolean
    isSelectMode: boolean
    orders: OrdersType
    orderDetails?: OrderDetailsForTitle
    handleOrderChange: (event: SelectChangeEvent<OrderIdType>) => void
    orderIdsLength?: number
    uid?: string
    id?: number
    variant?: SlipVariant
}

export const ReceiptAndSlipDialogHeader = ({
    isAuthenticated,
    isSelectMode,
    orders,
    orderDetails,
    handleOrderChange,
    variant = "receipt",
    orderIdsLength,
    uid,
    id,
}: SlipModalHeaderProps) => {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const selectedOrder = orders?.find(
        (order) => (uid !== undefined && order.uid === uid) || (id !== undefined && order.id === id),
    )
    const [searchQuery, setSearchQuery] = useState("")

    const filteredOrders = useMemo(
        () =>
            orders.filter((order) => {
                const searchLower = sanitizeSearchQuery(searchQuery ?? "").toLowerCase()
                if (!searchLower) return true

                const organizers = hasOrganizers(order) ? order.organizers : []
                const orderId = order.id?.toString() || ""
                const meetingName = order.meetingDetails?.meetingName?.toLowerCase() || ""
                const organizerNames = hasOrganizers(order)
                    ? organizers?.map((org) => org.name?.toLowerCase() || "").join(" ")
                    : ""
                const customerName = hasCustomer(order) ? order.customer.displayName.toLowerCase() : ""

                return (
                    orderId.includes(searchLower) ||
                    meetingName.includes(searchLower) ||
                    organizerNames.includes(searchLower) ||
                    customerName.includes(searchLower)
                )
            }),
        [searchQuery, orders],
    )

    if (isSelectMode) {
        return (
            <LocalizedStrict
                id={
                    variant === "slip"
                        ? "production-package-slip-header-orders-count"
                        : variant === "receipt"
                          ? "user-order-receipt-page-header-orders-count"
                          : "user-pos-order-receipt-page-header-orders-count"
                }
                vars={{ orderCount: orderIdsLength || 0 }}
            >
                <Typography className={classes.headerText}>Packing slips: {orderIdsLength} selected</Typography>
            </LocalizedStrict>
        )
    }

    if (isAuthenticated && orders.length > 1) {
        return (
            <Select
                // pos keeps its title hidden on mobile, so the picker would be an empty box there
                className={cx(classes.orderSelect, variant === "pos" && classes.orderSelectHiddenOnMobile)}
                value={selectedOrder?.id ?? ""}
                onChange={handleOrderChange}
                renderValue={(value) => <SelectedOrderTitle value={value} orders={orders} variant={variant} />}
                displayEmpty={false}
                variant="outlined"
                MenuProps={{
                    PaperProps: {
                        className: classes.orderSelectItem,
                    },
                }}
            >
                <SearchOrdersInPopup searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                {filteredOrders.map((order) => (
                    <MenuItem divider key={order.id} value={order.id} className={classes.menuItem}>
                        <ReceiptDisplayDropdownMenu order={order} />
                    </MenuItem>
                ))}
            </Select>
        )
    }

    if (isAuthenticated && orders.length === 1) {
        return <SelectedOrderTitle value={selectedOrder?.id ?? orders[0].id} orders={orders} variant={variant} />
    }

    if (orderDetails) {
        if (variant === "pos") {
            const deliveryTime = orderDetails.deliveries?.[0]?.deliveryTime
            const orderDate = deliveryTime
                ? dateTimeFactory.formatCustom(dateTimeFactory.fromApi(deliveryTime), "MMM d'th', yyyy")
                : ""

            return (
                <LocalizedStrict id="receipt-order-label" vars={{ orderId: orderDetails.id.toString(), orderDate }}>
                    <Typography className={classes.headerText}>
                        Receipt #{orderDetails.id.toString()}, {orderDate}
                    </Typography>
                </LocalizedStrict>
            )
        }

        return (
            <Typography className={classes.headerText}>
                {[orderDetails.meetingDetails?.meetingName, `#${orderDetails.id}`].filter(Boolean).join(", ")}
            </Typography>
        )
    }

    return <Box className={classes.layoutBlankFill} />
}
